import {
	Box,
	Button,
	Flex,
	Grid,
	HStack,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Switch,
	Text,
	VStack,
} from '@chakra-ui/react';
import { FC, useCallback, useMemo, useState } from 'react';

import { CheckOutline, TrashOutline } from '@bonusly/ds-icon';

import { useCustomTheme } from '../hooks/use-custom-theme';
import { getPresetsByCategory, ThemePreset } from '../theme/custom-theme-presets';
import { generateColorScale, generateFullThemeFromColor, generateThemeFromColor, isValidHexColor } from '../theme/theme-generator';

interface CustomThemeModalProps {
	isOpen: boolean;
	onClose: () => void;
}

/**
 * Color preview swatch for a theme preset
 */
const PresetSwatch: FC<{ preset: ThemePreset; isSelected: boolean; onClick: () => void; onDelete?: () => void }> = ({
	preset,
	isSelected,
	onClick,
	onDelete,
}) => {
	return (
		<Box
			as="button"
			onClick={onClick}
			position="relative"
			borderRadius="xl"
			overflow="hidden"
			border="2px solid"
			borderColor={isSelected ? 'primary.fg' : 'border.default'}
			transition="all 0.2s"
			_hover={{
				borderColor: isSelected ? 'primary.fg' : 'border.strong',
				transform: 'scale(1.02)',
			}}
			_focus={{
				outline: 'none',
				boxShadow: 'outline',
			}}
			cursor="pointer"
			bg="bg.surface"
		>
			{/* Color preview bars */}
			<Flex h="48px" w="full">
				<Box flex={1} bg={preset.preview.primary} />
				<Box flex={1} bg={preset.preview.secondary} />
				<Box flex={1} bg={preset.preview.accent} />
			</Flex>

			{/* Label */}
			<Box px={3} py={2} bg="bg.surface">
				<HStack justify="space-between">
					<Text fontSize="sm" fontWeight="medium" color="text.default" textAlign="left" noOfLines={1}>
						{preset.name}
					</Text>
					{onDelete && (
						<Box
							as="span"
							onClick={(e: React.MouseEvent) => {
								e.stopPropagation();
								onDelete();
							}}
							p={1}
							borderRadius="md"
							_hover={{ bg: 'danger.bgAlpha' }}
							color="text.softer"
						>
							<TrashOutline boxSize={3} />
						</Box>
					)}
				</HStack>
			</Box>

			{/* Selected indicator */}
			{isSelected && (
				<Box position="absolute" top={2} right={2} bg="primary.bg" borderRadius="full" p={1}>
					<CheckOutline boxSize={3} color="white" />
				</Box>
			)}
		</Box>
	);
};

/**
 * Default theme option (no custom theme)
 */
const DefaultOption: FC<{ isSelected: boolean; onClick: () => void }> = ({ isSelected, onClick }) => {
	return (
		<Box
			as="button"
			onClick={onClick}
			position="relative"
			borderRadius="xl"
			overflow="hidden"
			border="2px solid"
			borderColor={isSelected ? 'primary.fg' : 'border.default'}
			transition="all 0.2s"
			_hover={{
				borderColor: isSelected ? 'primary.fg' : 'border.strong',
				transform: 'scale(1.02)',
			}}
			_focus={{
				outline: 'none',
				boxShadow: 'outline',
			}}
			cursor="pointer"
			bg="bg.surface"
		>
			{/* Default blue color preview */}
			<Flex h="48px" w="full">
				<Box flex={1} bg="#1670df" />
				<Box flex={1} bg="#1358b9" />
				<Box flex={1} bg="#14458a" />
			</Flex>

			{/* Label */}
			<Box px={3} py={2} bg="bg.surface">
				<Text fontSize="sm" fontWeight="medium" color="text.default" textAlign="left">
					Default
				</Text>
			</Box>

			{/* Selected indicator */}
			{isSelected && (
				<Box position="absolute" top={2} right={2} bg="primary.bg" borderRadius="full" p={1}>
					<CheckOutline boxSize={3} color="white" />
				</Box>
			)}
		</Box>
	);
};

// Preset colors for the quick-pick palette
const QUICK_COLORS = [
	'#e53e3e', // red
	'#dd6b20', // orange
	'#d69e2e', // yellow
	'#38a169', // green
	'#319795', // teal
	'#3182ce', // blue
	'#805ad5', // purple
	'#d53f8c', // pink
	'#718096', // gray
	'#1a1a1a', // black
];

/**
 * Color Generator Section
 */
const ColorGenerator: FC<{
	onGenerate: (color: string, name: string, fullTheme: boolean) => void;
}> = ({ onGenerate }) => {
	const [color, setColor] = useState('#805ad5');
	const [name, setName] = useState('');
	const [fullTheme, setFullTheme] = useState(false);

	const isValid = useMemo(() => isValidHexColor(color), [color]);

	const previewScale = useMemo(() => {
		if (!isValid) return null;
		return generateColorScale(color);
	}, [color, isValid]);

	const previewTheme = useMemo(() => {
		if (!isValid) return null;
		return fullTheme ? generateFullThemeFromColor(color, name || 'Preview') : generateThemeFromColor(color, name || 'Preview');
	}, [color, name, fullTheme, isValid]);

	const handleGenerate = useCallback(() => {
		if (isValid) {
			onGenerate(color, name || 'Custom', fullTheme);
		}
	}, [color, name, fullTheme, isValid, onGenerate]);

	return (
		<Box p={4} borderRadius="xl" bg="bg.soft" border="1px solid" borderColor="border.soft">
			<VStack spacing={4} align="stretch">
				{/* Color picker row */}
				<HStack spacing={4}>
					{/* Native color picker */}
					<Box position="relative">
						<Input
							type="color"
							value={color}
							onChange={(e) => setColor(e.target.value)}
							w="56px"
							h="56px"
							p={0}
							border="none"
							borderRadius="lg"
							cursor="pointer"
							sx={{
								'&::-webkit-color-swatch-wrapper': { p: 0 },
								'&::-webkit-color-swatch': { borderRadius: '8px', border: '2px solid', borderColor: 'border.default' },
							}}
						/>
					</Box>

					{/* Hex input */}
					<VStack align="stretch" flex={1} spacing={1}>
						<Text fontSize="xs" fontWeight="medium" color="text.soft">
							Hex Color
						</Text>
						<Input
							value={color}
							onChange={(e) => setColor(e.target.value)}
							placeholder="#805ad5"
							size="sm"
							borderRadius="md"
							fontFamily="mono"
							isInvalid={!isValid && color.length > 0}
						/>
					</VStack>

					{/* Name input */}
					<VStack align="stretch" flex={1} spacing={1}>
						<Text fontSize="xs" fontWeight="medium" color="text.soft">
							Theme Name
						</Text>
						<Input value={name} onChange={(e) => setName(e.target.value)} placeholder="My Theme" size="sm" borderRadius="md" />
					</VStack>
				</HStack>

				{/* Quick color palette */}
				<Box>
					<Text fontSize="xs" fontWeight="medium" color="text.soft" mb={2}>
						Quick Pick
					</Text>
					<HStack spacing={2} flexWrap="wrap">
						{QUICK_COLORS.map((c) => (
							<Box
								key={c}
								as="button"
								w="28px"
								h="28px"
								borderRadius="md"
								bg={c}
								border="2px solid"
								borderColor={color === c ? 'primary.fg' : 'transparent'}
								cursor="pointer"
								transition="all 0.15s"
								_hover={{ transform: 'scale(1.1)' }}
								onClick={() => setColor(c)}
							/>
						))}
					</HStack>
				</Box>

				{/* Preview */}
				{previewTheme && previewScale && (
					<Box>
						<Text fontSize="xs" fontWeight="medium" color="text.soft" mb={2}>
							Preview
						</Text>
						<Flex h="40px" borderRadius="lg" overflow="hidden">
							<Box flex={1} bg={previewTheme.preview.primary} />
							<Box flex={1} bg={previewTheme.preview.secondary} />
							<Box flex={1} bg={previewTheme.preview.accent} />
							<Box flex={1} bg={previewScale['300']} />
							<Box flex={1} bg={previewScale['200']} />
						</Flex>
					</Box>
				)}

				{/* Full theme toggle + Generate button */}
				<HStack justify="space-between">
					<HStack spacing={2}>
						<Switch size="sm" isChecked={fullTheme} onChange={(e) => setFullTheme(e.target.checked)} colorScheme="blue" />
						<Text fontSize="sm" color="text.soft">
							Full theme (dark backgrounds)
						</Text>
					</HStack>

					<Button size="sm" colorScheme="blue" onClick={handleGenerate} isDisabled={!isValid}>
						Apply Theme
					</Button>
				</HStack>
			</VStack>
		</Box>
	);
};

/**
 * CustomThemeModal
 *
 * A modal that allows users to select from preset color themes.
 * Themes are organized into two categories:
 * - Accent Colors: Simple primary color swaps
 * - Full Themes: Complete palette overrides
 *
 * Now includes a color generator for creating custom themes!
 */
export const CustomThemeModal: FC<CustomThemeModalProps> = ({ isOpen, onClose }) => {
	const { activePresetId, setTheme, clearTheme, isActive, generateAndApplyTheme, userThemes, removeUserTheme } = useCustomTheme();

	const accentPresets = getPresetsByCategory('accent');
	const fullPresets = getPresetsByCategory('full');

	// Generate ThemePreset objects from user themes for display
	const userThemePresets = useMemo(() => {
		return userThemes.map((ut) => {
			const isFullTheme = ut.id.startsWith('user-full-');
			return isFullTheme ? generateFullThemeFromColor(ut.baseColor, ut.name) : generateThemeFromColor(ut.baseColor, ut.name);
		});
	}, [userThemes]);

	const handleSelectPreset = (presetId: string) => {
		setTheme(presetId);
	};

	const handleSelectDefault = () => {
		clearTheme();
	};

	const handleGenerateTheme = useCallback(
		(color: string, name: string, fullTheme: boolean) => {
			generateAndApplyTheme(color, name, fullTheme);
		},
		[generateAndApplyTheme]
	);

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered scrollBehavior="inside">
			<ModalOverlay bg="bg.overlayBackdrop" backdropFilter="blur(4px)" />
			<ModalContent borderRadius="2xl" bg="bg.surface" maxH="85vh">
				<ModalHeader pb={2}>
					<Text fontSize="xl" fontWeight="semibold">
						Customize Theme
					</Text>
					<Text fontSize="sm" color="text.soft" fontWeight="normal" mt={1}>
						Choose a preset or create your own color theme
					</Text>
				</ModalHeader>
				<ModalCloseButton borderRadius="full" />

				<ModalBody py={4} overflowY="auto">
					<VStack spacing={6} align="stretch">
						{/* Color Generator */}
						<Box>
							<Text fontSize="sm" fontWeight="semibold" color="text.soft" mb={3} textTransform="uppercase" letterSpacing="wide">
								Create Your Own
							</Text>
							<ColorGenerator onGenerate={handleGenerateTheme} />
						</Box>

						{/* User Created Themes */}
						{userThemePresets.length > 0 && (
							<Box>
								<Text fontSize="sm" fontWeight="semibold" color="text.soft" mb={3} textTransform="uppercase" letterSpacing="wide">
									Your Themes
								</Text>
								<Grid templateColumns="repeat(auto-fill, minmax(140px, 1fr))" gap={3}>
									{userThemePresets.map((preset) => (
										<PresetSwatch
											key={preset.id}
											preset={preset}
											isSelected={isActive(preset.id)}
											onClick={() => {
												// Find the matching user theme and apply it
												const userTheme = userThemes.find((ut) => ut.id === preset.id);
												if (userTheme) {
													generateAndApplyTheme(userTheme.baseColor, userTheme.name, preset.id.startsWith('user-full-'));
												}
											}}
											onDelete={() => removeUserTheme(preset.id)}
										/>
									))}
								</Grid>
							</Box>
						)}

						{/* Default Option */}
						<Box>
							<Text fontSize="sm" fontWeight="semibold" color="text.soft" mb={3} textTransform="uppercase" letterSpacing="wide">
								Default
							</Text>
							<Grid templateColumns="repeat(auto-fill, minmax(140px, 1fr))" gap={3}>
								<DefaultOption isSelected={activePresetId === null} onClick={handleSelectDefault} />
							</Grid>
						</Box>

						{/* Accent Colors */}
						<Box>
							<Text fontSize="sm" fontWeight="semibold" color="text.soft" mb={3} textTransform="uppercase" letterSpacing="wide">
								Accent Colors
							</Text>
							<Grid templateColumns="repeat(auto-fill, minmax(140px, 1fr))" gap={3}>
								{accentPresets.map((preset) => (
									<PresetSwatch key={preset.id} preset={preset} isSelected={isActive(preset.id)} onClick={() => handleSelectPreset(preset.id)} />
								))}
							</Grid>
						</Box>

						{/* Full Themes */}
						<Box>
							<Text fontSize="sm" fontWeight="semibold" color="text.soft" mb={3} textTransform="uppercase" letterSpacing="wide">
								Full Themes
							</Text>
							<Grid templateColumns="repeat(auto-fill, minmax(140px, 1fr))" gap={3}>
								{fullPresets.map((preset) => (
									<PresetSwatch key={preset.id} preset={preset} isSelected={isActive(preset.id)} onClick={() => handleSelectPreset(preset.id)} />
								))}
							</Grid>
						</Box>
					</VStack>
				</ModalBody>

				<ModalFooter borderTop="1px solid" borderColor="border.soft" pt={4}>
					<HStack spacing={3}>
						<Button variant="ghost" onClick={onClose}>
							Close
						</Button>
					</HStack>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

CustomThemeModal.displayName = 'CustomThemeModal';
